import BASE_URL from "../../config/api";
import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ImageSkeleton from "./ImageSkeleton";
import { BsChevronLeft, BsChevronRight } from "react-icons/bs";

const LIMIT = 20;

const TABS = [
  { label: "All", value: null, path: "/flat" },
  { label: "2 Bed", value: "2-bedroom", path: "/flat/2-bedroom" },
  { label: "3 Bed", value: "3-bedroom", path: "/flat/3-bedroom" },
  { label: "4 Bed", value: "4-bedroom", path: "/flat/4-bedroom" },
  { label: "5 Bed", value: "5-bedroom", path: "/flat/5-bedroom" },
  { label: "6+ Bed", value: "6-bedroom", path: "/flat/6-bedroom" },
];

const PlanCard = ({ drawing }) => (
  <Link
    to={`/house-details/${drawing._id}`}
    className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl transition-all duration-300 flex flex-col"
  >
    <div className="relative overflow-hidden h-56 md:h-64 flex-shrink-0">
      <img src={drawing.frontElevation} alt={drawing.description || "House plan"} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
        <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#1877F2] text-white text-sm font-bold px-6 py-2.5 rounded-full">View Plan →</span>
      </div>
      <span className="absolute top-3 left-3 bg-white/90 backdrop-blur-sm text-[#1877F2] text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide shadow-sm">{drawing.refNo}</span>
    </div>
    <div className="p-5 flex flex-col flex-1">
      <h3 className="font-bold text-gray-900 text-sm md:text-base uppercase leading-snug line-clamp-2 flex-1">{drawing.description}</h3>
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
        <div>
          <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Price</p>
          <p className="text-lg font-bold text-[#1877F2]">₦{(Number((drawing.price || "").toString().replace(/[^0-9.]/g, "")) || 0).toLocaleString()}</p>
        </div>
        <span className="text-xs font-semibold text-[#1877F2] border border-[#1877F2] rounded-full px-4 py-1.5 group-hover:bg-[#1877F2] group-hover:text-white transition-colors duration-200">View Plan</span>
      </div>
    </div>
  </Link>
);

const Pagination = ({ page, totalPages, setPage }) => {
  if (totalPages <= 1) return null;
  const pages = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    if (page <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
    if (page >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", page - 1, page, page + 1, "...", totalPages];
  };
  return (
    <div className="flex items-center justify-center gap-2 mt-12">
      <button onClick={() => setPage((p) => p - 1)} disabled={page === 1} className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#1877F2] hover:text-[#1877F2] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><BsChevronLeft className="text-xs" /></button>
      {pages().map((p, i) => p === "..." ? <span key={`e-${i}`} className="w-9 h-9 flex items-center justify-center text-gray-400 text-sm">…</span> : <button key={p} onClick={() => setPage(p)} className={`w-9 h-9 flex items-center justify-center rounded-full text-sm font-semibold transition-colors ${page === p ? "bg-[#1877F2] text-white shadow-sm" : "border border-gray-200 text-gray-600 hover:border-[#1877F2] hover:text-[#1877F2]"}`}>{p}</button>)}
      <button onClick={() => setPage((p) => p + 1)} disabled={page === totalPages} className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#1877F2] hover:text-[#1877F2] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"><BsChevronRight className="text-xs" /></button>
    </div>
  );
};

const FlatCategory = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [drawings, setDrawings] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      setDrawings([]);
      setPage(1);
      try {
        const res = await axios.get(`${BASE_URL}/drawing/search-drawings/search?type=flat&category=${category}`);
        setDrawings(res.data.data ?? []);
      } catch { setDrawings([]); } finally { setLoading(false); }
    };
    fetch();
  }, [category]);

  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page]);

  const label = category ? category.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "";
  const totalPages = Math.ceil(drawings.length / LIMIT);
  const paginated = drawings.slice((page - 1) * LIMIT, page * LIMIT);

  return (
    <div className="font-Montserrat min-h-screen bg-gray-50">
      <div className="h-[56px] md:h-[72px]" />
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 md:px-10 py-10 md:py-16">
          <div className="text-center mb-8">
            <p className="text-[#1877F2] uppercase tracking-[0.3em] text-xs font-semibold mb-3">Flats &amp; Apartments</p>
            <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">{label} Flats</h1>
            <p className="text-gray-500 text-sm md:text-base max-w-xl mx-auto">Browse all our {label.toLowerCase()} flat and apartment designs.</p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            {TABS.map((tab) => (
              <button key={tab.label} onClick={() => navigate(tab.path)}
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-colors duration-200 ${category === tab.value ? "bg-[#1877F2] text-white border-[#1877F2]" : "bg-white text-gray-600 border-gray-200 hover:border-[#1877F2] hover:text-[#1877F2]"}`}>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-10 py-10">
        {loading ? <ImageSkeleton /> : paginated.length === 0 ? (
          <div className="text-center py-24 text-gray-400">
            <p className="text-4xl mb-4">🏠</p>
            <p className="text-lg font-semibold">No plans available for this category</p>
            <Link to="/flat" className="mt-4 inline-block text-[#1877F2] underline text-sm font-medium">View all flats</Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-400 mb-6">
              Showing <span className="font-semibold text-gray-700">{(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, drawings.length)}</span> of <span className="font-semibold text-gray-700">{drawings.length}</span> plans
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginated.map((drawing, i) => <PlanCard key={i} drawing={drawing} />)}
            </div>
            <Pagination page={page} totalPages={totalPages} setPage={setPage} />
          </>
        )}
      </div>
    </div>
  );
};

export default FlatCategory;
